import { useState } from 'react';
import { useResource } from '../../hooks/useResource';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import type { AutomationRow } from '../../lib/types';
import { sx } from './styles';

type EventKind =
  | 'conversation_created'
  | 'message_received'
  | 'status_changed'
  | 'labels_changed';

const EVENTS: EventKind[] = [
  'conversation_created',
  'message_received',
  'status_changed',
  'labels_changed',
];

const COND_OPS = ['eq', 'neq', 'in', 'contains', 'regex'] as const;
type CondOp = (typeof COND_OPS)[number];

interface Condition {
  field: string;
  op: CondOp;
  value?: unknown;
}

const ACTION_TYPES = ['set_status', 'add_label', 'assign', 'post_message', 'run_macro'] as const;
type ActionType = (typeof ACTION_TYPES)[number];

interface Action {
  type: ActionType;
  params: Record<string, unknown>;
}

interface DraftRule {
  id?: string;
  name: string;
  event: EventKind;
  conditions: Condition[];
  actions: Action[];
  enabled: boolean;
}

const EMPTY: DraftRule = {
  name: '',
  event: 'conversation_created',
  conditions: [],
  actions: [],
  enabled: true,
};

function eventLabelKey(e: EventKind): string {
  switch (e) {
    case 'conversation_created':
      return 'settings.automations.event.created';
    case 'message_received':
      return 'settings.automations.event.message';
    case 'status_changed':
      return 'settings.automations.event.status';
    case 'labels_changed':
      return 'settings.automations.event.labels';
  }
}

export function AutomationBuilder({ canEdit }: { canEdit: boolean }) {
  const { items, loading } = useResource<AutomationRow>('automation_rules');
  const [draft, setDraft] = useState<DraftRule | null>(null);
  const [saving, setSaving] = useState(false);

  function startEdit(row: AutomationRow) {
    setDraft({
      id: row.id,
      name: row.name,
      event: (row.event as EventKind) || 'conversation_created',
      conditions: (row.conditions as Condition[]) || [],
      actions: (row.actions as Action[]) || [],
      enabled: row.enabled,
    });
  }

  function startCreate() {
    setDraft({ ...EMPTY, name: 'New rule' });
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    try {
      const body = {
        name: draft.name,
        event: draft.event,
        conditions: draft.conditions,
        actions: draft.actions,
        enabled: draft.enabled,
      };
      if (draft.id) {
        await api.patch('automations', draft.id, body);
      } else {
        await api.create('automations', body);
      }
      setDraft(null);
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    if (!confirm(t('confirm.delete'))) return;
    try {
      await api.delete('automations', id);
    } catch (e) {
      alert(`Delete failed: ${e instanceof Error ? e.message : e}`);
    }
  }

  function setCond(idx: number, patch: Partial<Condition>) {
    setDraft((d) =>
      d
        ? {
            ...d,
            conditions: d.conditions.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
          }
        : d
    );
  }

  function setAction(idx: number, patch: Partial<Action>) {
    setDraft((d) =>
      d
        ? {
            ...d,
            actions: d.actions.map((a, i) => (i === idx ? { ...a, ...patch } : a)),
          }
        : d
    );
  }

  return (
    <section>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>{t('settings.automations.title')}</h2>
        {canEdit && !draft && (
          <button onClick={startCreate} style={sx.btnPrimary}>
            + {t('common.new')}
          </button>
        )}
      </div>
      {loading && <div>{t('common.loading')}</div>}

      {!draft && (
        <table style={sx.table}>
          <thead>
            <tr>
              <th style={sx.th}>{t('common.name')}</th>
              <th style={sx.th}>{t('settings.automations.trigger')}</th>
              <th style={sx.th}>{t('common.enabled')}</th>
              <th style={sx.th}>{t('settings.automations.conditions')}</th>
              <th style={sx.th}>{t('settings.automations.actions')}</th>
              <th style={sx.th}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id}>
                <td style={sx.td}>{r.name}</td>
                <td style={sx.td}>{r.event}</td>
                <td style={sx.td}>{r.enabled ? t('common.yes') : t('common.no')}</td>
                <td style={sx.td}>{(r.conditions ?? []).length}</td>
                <td style={sx.td}>{(r.actions ?? []).length}</td>
                <td style={sx.td}>
                  {canEdit && (
                    <>
                      <button onClick={() => startEdit(r)} style={{ ...sx.btnSecondary, marginRight: 4 }}>
                        {t('common.edit')}
                      </button>
                      <button onClick={() => remove(r.id)} style={sx.btnDanger}>
                        {t('common.delete')}
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {draft && (
        <div>
          <div style={sx.card}>
            <label style={sx.label}>{t('common.name')}</label>
            <input
              type="text"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              style={sx.input}
            />
            <label style={{ ...sx.label, marginTop: 12 }}>{t('settings.automations.trigger')}</label>
            <select
              value={draft.event}
              onChange={(e) => setDraft({ ...draft, event: e.target.value as EventKind })}
              style={{ ...sx.input, maxWidth: 320 }}
            >
              {EVENTS.map((e) => (
                <option key={e} value={e}>
                  {t(eventLabelKey(e))}
                </option>
              ))}
            </select>
            <label style={{ ...sx.label, marginTop: 12 }}>{t('common.enabled')}</label>
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })}
            />
          </div>

          <div style={sx.card}>
            <label style={sx.label}>{t('settings.automations.conditions')}</label>
            {draft.conditions.map((c, idx) => (
              <div key={idx} style={sx.row}>
                <input
                  type="text"
                  placeholder="field"
                  value={c.field}
                  onChange={(e) => setCond(idx, { field: e.target.value })}
                  style={{ ...sx.input, maxWidth: 200 }}
                />
                <select
                  value={c.op}
                  onChange={(e) => setCond(idx, { op: e.target.value as CondOp })}
                  style={{ ...sx.input, maxWidth: 120 }}
                >
                  {COND_OPS.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="value"
                  value={String(c.value ?? '')}
                  onChange={(e) => setCond(idx, { value: e.target.value })}
                  style={sx.input}
                />
                <button
                  onClick={() =>
                    setDraft({ ...draft, conditions: draft.conditions.filter((_, i) => i !== idx) })
                  }
                  style={sx.btnDanger}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              onClick={() =>
                setDraft({
                  ...draft,
                  conditions: [...draft.conditions, { field: '', op: 'eq', value: '' }],
                })
              }
              style={sx.btnSecondary}
            >
              + {t('settings.automations.addCondition')}
            </button>
          </div>

          <div style={sx.card}>
            <label style={sx.label}>{t('settings.automations.actions')}</label>
            {draft.actions.map((a, idx) => (
              <div key={idx} style={sx.row}>
                <select
                  value={a.type}
                  onChange={(e) => setAction(idx, { type: e.target.value as ActionType, params: {} })}
                  style={{ ...sx.input, maxWidth: 180 }}
                >
                  {ACTION_TYPES.map((tp) => (
                    <option key={tp} value={tp}>
                      {tp}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="params (JSON)"
                  value={JSON.stringify(a.params)}
                  onChange={(e) => {
                    try {
                      setAction(idx, { params: JSON.parse(e.target.value || '{}') });
                    } catch {
                      // ignore parse errors mid-edit
                    }
                  }}
                  style={sx.input}
                />
                <button
                  onClick={() =>
                    setDraft({ ...draft, actions: draft.actions.filter((_, i) => i !== idx) })
                  }
                  style={sx.btnDanger}
                >
                  ×
                </button>
              </div>
            ))}
            <button
              onClick={() =>
                setDraft({
                  ...draft,
                  actions: [...draft.actions, { type: 'add_label', params: {} }],
                })
              }
              style={sx.btnSecondary}
            >
              + {t('settings.automations.addAction')}
            </button>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={save} disabled={saving} style={sx.btnPrimary}>
              {saving ? t('common.loading') : t('common.save')}
            </button>
            <button onClick={() => setDraft(null)} style={sx.btnSecondary}>
              {t('common.cancel')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
